import {
  ArrowPathIcon,
  MagnifyingGlassIcon,
  XMarkIcon
} from "@heroicons/react/24/outline";
import { FC, useState } from "react";
import useFetch from "../../../hooks/useFetch";
import { IEsimInstallationDiagnostics } from "../../../interfaces/esim/EsimDiagnostics.types";
import { getEsimProfileDiagnostics } from "../../../services/Esim.service";
import Button from "../../ui/Button";
import IconBtn from "../../ui/IconBtn";
import Modal from "../../ui/Modal";
import ConnectivityCard from "./ConnectivityCard";
import DeviceCard from "./DeviceCard";
import ProblemFixCallout from "./ProblemFixCallout";
import SmdpTimeline from "./SmdpTimeline";
import StageStepper from "./StageStepper";

// Trigger button (lives at the top of the profile page) that opens a modal with the live
// diagnostics. OCS is hit only on first open + explicit "Rifresko" — never on page load.
const DiagnosticsPanel: FC<{ profileId: number }> = ({ profileId }) => {
  const [open, setOpen] = useState(false);
  const { response, loading, error, handleFunc } = useFetch<
    IEsimInstallationDiagnostics,
    number
  >(getEsimProfileDiagnostics);

  const data = response?.data;
  const hasRun = !!response || !!error;

  const fetchData = () => {
    void handleFunc(profileId);
  };

  const openModal = () => {
    setOpen(true);
    if (!hasRun) fetchData();
  };

  return (
    <>
      <Button
        onClick={openModal}
        className="flex items-center gap-2 whitespace-nowrap"
      >
        <MagnifyingGlassIcon className="w-4 h-4" />
        Diagnostiko
      </Button>

      <Modal
        show={open}
        onClose={() => setOpen(false)}
        position="center"
        modalWidth="760px"
        hasHeader={false}
      >
        <div className="p-5 flex flex-col gap-4 bg-gray-50 min-h-full">
          <div className="flex items-center justify-between gap-4">
            <h3 className="text-lg font-semibold">
              Diagnostikim i instalimit & lidhshmërisë
            </h3>
            <div className="flex items-center gap-2">
              <Button
                onClick={fetchData}
                disabled={loading}
                btnType="custom"
                className={`border border-gray-300 text-gray-700 flex items-center
                  gap-2 whitespace-nowrap text-sm`}
              >
                <ArrowPathIcon
                  className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                />
                Rifresko
              </Button>
              <IconBtn onClick={() => setOpen(false)}>
                <XMarkIcon className="w-5 h-5" />
              </IconBtn>
            </div>
          </div>

          {loading && (
            <p className="text-sm text-gray-500">Duke marrë të dhënat nga OCS…</p>
          )}

          {!loading && !!error && (
            <p className="text-sm text-red">
              Ndodhi një gabim gjatë marrjes së diagnostikimit. Provoni sërish.
            </p>
          )}

          {!loading && data && (
            <div className="flex flex-col gap-4">
              {data.liveDataUnavailable && (
                <div className="border-l-4 border-orange bg-orange/5 p-3 rounded text-sm text-gray-700">
                  {data.liveDataUnavailableReason ||
                    "Të dhënat e drejtpërdrejta nga OCS nuk janë të disponueshme për momentin."}{" "}
                  Po shfaqet statusi i fundit i ruajtur.
                </div>
              )}

              <ProblemFixCallout hints={data.hints} />

              <div className="bg-white p-4 rounded-lg shadow-sm">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">
                  Faza e instalimit
                </h4>
                <StageStepper stage={data.stage} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ConnectivityCard data={data} />
                <DeviceCard
                  device={data.device}
                  subscriberStatus={data.subscriberStatus}
                />
              </div>

              <SmdpTimeline entries={data.smdpTimeline} />
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export default DiagnosticsPanel;
